import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AUTH_TOKEN_STORAGE_KEY,
  clearSessionAuthToken,
  setSessionAuthToken,
} from '../utils/authFetch';
import {
  flushAuthSessionHandlers,
  registerAuthSessionHandlers,
  suppressSessionInvalidation,
} from '../utils/authSession';
import {
  ACCOUNT_DEACTIVATED_MESSAGE,
  isAccountStatusDeactivated,
} from '../utils/accountStatus';
import { extractPersistableLoginToken } from '../utils/loginToken';
import { useLoginMutation } from '../redux/authApi';
import { useGetUserProfileQuery } from '../redux/complaintApi';
import { store } from '../redux/store';
import { baseApi } from '../redux/baseApi';
import {
  accountableDepartmentIdsFromAccount,
  accountableDepartmentLabelFromAccount,
  departmentNameFromAccount,
  inferAuthArea,
  inferAuthRoleKey,
  roleNameFromAccount,
  roleTierLabelFromAccount,
} from '../utils/accountRoleClassification';
import {
  canonicalPermissionKey,
  menuActionGranted,
  menuPermissionsFromAccountPayload,
  mergeModulePermissions,
  mergePermissionMaps,
  moduleAccessGranted,
  normalizeMenuPermissions,
  payloadHasMenuPermissions,
  permissionsFromAccountPayload,
} from '../utils/permissions';
import {
  canAccessBehaviourTracking,
  isReportingSupervisorValue,
} from '../utils/behaviourAccess';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('groverSweets_user');
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      if (!parsed) return null;
      const menuPermissions = normalizeMenuPermissions(
        parsed?.menuPermissions ?? parsed?.menu_permissions
      );
      const permissions = mergeModulePermissions(
        parsed?.permissions,
        menuPermissions
      );
      const roleName =
        roleNameFromAccount(parsed) ||
        parsed?.roleName ||
        parsed?.role_name ||
        roleTierLabelFromAccount(parsed);
      const departmentName =
        departmentNameFromAccount(parsed) ||
        parsed?.departmentName ||
        parsed?.department_name ||
        '';
      const roleType = parsed?.roleType ?? parsed?.role_type ?? '';
      const roleKey = inferAuthRoleKey({
        roleName,
        roleType,
      });
      const isReportingSupervisor = isReportingSupervisorValue(
        parsed?.isReportingSupervisor ?? parsed?.is_reporting_supervisor
      );
      return {
        ...parsed,
        permissions,
        menuPermissions,
        roleName,
        departmentName,
        roleType,
        isReportingSupervisor,
        area: inferAuthArea({
          roleName,
          roleType,
        }),
        roleKey,
      };
    } catch {
      return null;
    }
  });

  const [loginMutation] = useLoginMutation();

  const logout = useCallback(() => {
    setUser(null);
    clearSessionAuthToken();
    localStorage.removeItem('groverSweets_user');
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    try {
      store.dispatch(baseApi.util.resetApiState());
    } catch {
      /* ignore */
    }
  }, []);

  const logoutDeactivatedAccount = useCallback(() => {
    logout();
    toast.error(ACCOUNT_DEACTIVATED_MESSAGE);
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const employeeCode = user?.employeeCode;

  useEffect(() => {
    if (!user) return;
    if (isAccountStatusDeactivated(user.status)) {
      logoutDeactivatedAccount();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once for restored session

  const { data: userProfileRes } = useGetUserProfileQuery(employeeCode, {
    skip: !employeeCode,
    pollingInterval: employeeCode ? 60000 : 0,
  });

  useEffect(() => {
    const profile = userProfileRes?.data;
    if (!profile || !user) return;
    if (isAccountStatusDeactivated(profile.status)) {
      logoutDeactivatedAccount();
      return;
    }

    const profileHasMenu = payloadHasMenuPermissions({ data: profile });
    const incomingMenu = profileHasMenu
      ? menuPermissionsFromAccountPayload({ data: profile })
      : {};
    const incomingPermissions = permissionsFromAccountPayload({ data: profile });
    const roleType = profile.role_type ?? '';
    const isReportingSupervisor = isReportingSupervisorValue(
      profile.is_reporting_supervisor ?? profile.isReportingSupervisor
    );

    setUser((prev) => {
      if (!prev) return prev;

      const menuPermissions = profileHasMenu
        ? mergePermissionMaps(prev.menuPermissions, incomingMenu)
        : prev.menuPermissions;
      const permissions = mergePermissionMaps(
        prev.permissions,
        Object.keys(incomingPermissions).length ? incomingPermissions : {}
      );
      const roleTypeUnchanged = (prev.roleType ?? '') === roleType;
      const supervisorUnchanged =
        Boolean(prev.isReportingSupervisor) === isReportingSupervisor;
      const permissionsUnchanged =
        JSON.stringify(prev.permissions ?? {}) === JSON.stringify(permissions);
      const menuPermissionsUnchanged =
        JSON.stringify(prev.menuPermissions ?? {}) === JSON.stringify(menuPermissions);

      if (
        permissionsUnchanged &&
        menuPermissionsUnchanged &&
        roleTypeUnchanged &&
        supervisorUnchanged
      ) {
        return prev;
      }

      const updated = {
        ...prev,
        permissions,
        menuPermissions,
        status: profile.status,
        isReportingSupervisor,
        ...(roleType ? { roleType } : {}),
      };
      localStorage.setItem('groverSweets_user', JSON.stringify(updated));
      return updated;
    });
  }, [userProfileRes, user, logoutDeactivatedAccount]);

  const login = useCallback(
    async (employeeCode, password) => {
      const payload = {
        employee_id: employeeCode,
        password,
      };

      const result = await loginMutation(payload).unwrap();

      /**
       * Be tolerant to different client wrappers:
       * - { success, message, data: { ...user } }
       * - { data: { success, message, data: { ...user } } } (nested)
       * - { ...user } (already unwrapped)
       */
      const data =
        result?.data?.data ??
        result?.data ??
        (result?.success ? result?.data : null) ??
        result ??
        {};

      if (isAccountStatusDeactivated(data?.status)) {
        clearSessionAuthToken();
        localStorage.removeItem('groverSweets_user');
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        const deactivatedError = new Error(ACCOUNT_DEACTIVATED_MESSAGE);
        deactivatedError.data = { message: ACCOUNT_DEACTIVATED_MESSAGE };
        throw deactivatedError;
      }

      const permissions = permissionsFromAccountPayload({ ...result, data });
      const menuPermissions = menuPermissionsFromAccountPayload({ ...result, data });
      const accountableDepartmentName = accountableDepartmentLabelFromAccount(data);
      const roleName = roleNameFromAccount(data) || roleTierLabelFromAccount(data);
      const departmentName = departmentNameFromAccount(data);
      const roleType = data?.role_type ?? '';
      const area = inferAuthArea({
        roleName,
        roleType,
      });
      const roleKey = inferAuthRoleKey({
        roleName,
        roleType,
      });

      const userData = {
        name: data?.name || 'User Name',
        email: data?.email || 'user@demoemail.com',
        employeeCode: data?.employee_id || employeeCode,
        id: data?.id,
        roleId: data?.role_id,
        roleName: roleName || data?.role_name,
        roleType: data?.role_type ?? '',
        isReportingSupervisor: isReportingSupervisorValue(
          data?.is_reporting_supervisor ?? data?.isReportingSupervisor
        ),
        accountableDepartmentId:
          accountableDepartmentIdsFromAccount(data)[0] ||
          data?.accountable_department_id ||
          data?.accountableDepartmentId ||
          '',
        accountableDepartmentName,
        departmentName,
        permissions,
        menuPermissions,
        area,
        roleKey,
        status: data?.status,
      };

      setUser(userData);
      localStorage.setItem('groverSweets_user', JSON.stringify(userData));
      const authToken =
        (typeof result?.token === 'string' && result.token.trim()) ||
        extractPersistableLoginToken(result) ||
        extractPersistableLoginToken(data);
      if (authToken) {
        setSessionAuthToken(authToken);
        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, authToken);
      }
      suppressSessionInvalidation(1200);
      return userData;
    },
    [loginMutation]
  );

  const can = useCallback(
    (permissionKey) =>
      moduleAccessGranted(user?.menuPermissions, user?.permissions, permissionKey),
    [user]
  );

  const canAction = useCallback(
    (moduleKey, action) =>
      menuActionGranted(
        user?.menuPermissions,
        moduleKey,
        action,
        user?.permissions?.[canonicalPermissionKey(moduleKey)] ??
          user?.permissions?.[moduleKey]
      ),
    [user]
  );

  const area = useMemo(
    () =>
      user?.area ||
      inferAuthArea({
        roleName: user?.roleName,
        roleType: user?.roleType,
      }),
    [user]
  );

  const roleKey = useMemo(
    () =>
      user?.roleKey ||
      inferAuthRoleKey({
        roleName: user?.roleName,
        roleType: user?.roleType,
      }),
    [user]
  );

  const roleType = useMemo(() => user?.roleType ?? '', [user]);
  const isReportingSupervisor = useMemo(
    () =>
      isReportingSupervisorValue(
        user?.isReportingSupervisor ?? user?.is_reporting_supervisor
      ),
    [user]
  );
  const canBehaviourTrack = useMemo(
    () =>
      canAccessBehaviourTracking({
        roleType,
        roleKey,
        roleName: user?.roleName,
        isReportingSupervisor,
      }),
    [roleType, roleKey, user?.roleName, isReportingSupervisor]
  );

  useEffect(() => {
    registerAuthSessionHandlers({
      onSessionInvalidated: (message) => {
        logout();
        toast.error(message || 'Session expired. Please login again.');
        navigate('/login', { replace: true });
      },
    });
    return () => flushAuthSessionHandlers();
  }, [logout, navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: !!user,
        can,
        canAction,
        area,
        roleKey,
        roleType,
        isReportingSupervisor,
        canBehaviourTrack,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
